import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { LanguageSwitcher } from '@/components/features/auth/LanguageSwitcher';
import { ThemeToggle } from '@/components/features/auth/ThemeToggle';
import { getTenantFromDomain, getTenantDisplayName } from '@/utils/tenant';
import './LoginPage.css';

export const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const tenantCode = getTenantFromDomain();
  const tenantName = getTenantDisplayName(tenantCode);
  const tenantLogoUrl = `/v2/api/tenants/code/${tenantCode.toUpperCase()}/logo.png`;

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const onFinish = async (values: { username: string; password: string }) => {
    setLoading(true);
    try {
      await login(values);
      message.success(t('auth.loginSuccess'), 2);
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Login error:', error);

      if (error.message === '2FA_REQUIRED') {
        navigate('/auth/2fa');
      } else {
        // Show detailed error message
        let errorMessage = t('auth.loginError');

        if (error.response?.status === 401) {
          errorMessage = 'Neispravno korisničko ime ili lozinka';
        } else if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message && error.message !== '2FA_REQUIRED') {
          errorMessage = error.message;
        }

        // Show error message
        message.error({
          content: errorMessage,
          duration: 5,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Top controls */}
      <div className="login-top-controls">
        <LanguageSwitcher />
        <div style={{ marginLeft: 12 }}>
          <ThemeToggle />
        </div>
      </div>

      {/* Logo */}
      <div className="login-logo-wrapper">
        <img
          src={tenantLogoUrl}
          alt={`${tenantName} logo`}
          className="login-logo-img"
          onError={(e) => {
            // Fallback to text if logo fails to load
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const fallback = target.nextElementSibling as HTMLElement;
            if (fallback) fallback.style.display = 'flex';
          }}
        />
        <div className="login-logo" style={{ display: 'none' }}>
          {tenantCode.toUpperCase()}
        </div>
      </div>

      {/* Login Card */}
      <Card className="login-card">
        <h1 className="login-title">
          {t('auth.welcome')} {tenantName}
        </h1>

        <Form
          name="login"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
          size="large"
        >
          <Form.Item
            name="username"
            rules={[{ required: true, message: t('auth.usernameRequired') }]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder={t('auth.username')}
              autoFocus
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: t('auth.passwordRequired') }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={t('auth.password')}
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              {t('auth.login')}
            </Button>
          </Form.Item>
        </Form>

        <div className="login-forgot-password">
          <a href="/auth/forgot-password">{t('auth.forgotPassword')}</a>
        </div>
      </Card>

      {/* Footer */}
      <div className="login-footer">Powered by: ⚡ Thilo</div>
    </div>
  );
};
